import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, Slider, Space, Typography, message } from 'antd';
import { ShoppingCartOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import {
  MOBILE_CARD_IMAGE_HEIGHT,
  MOBILE_CARD_WIDTH,
  MOBILE_PHOTO_OUTPUT_SCALE,
  Offset,
  Size,
  centredOffset,
  clampOffset,
  containScale,
  coverScale,
  destRect,
  zoomAboutCentre,
} from '../../utils/mobileCrop';

const { Text } = Typography;

const FRAME: Size = { width: MOBILE_CARD_WIDTH, height: MOBILE_CARD_IMAGE_HEIGHT };
const MAX_ZOOM = 3;
/** The mobile photo upload accepts up to 1 MB. */
const MAX_UPLOAD_BYTES = 1024 * 1024;

interface MobileCardPreviewProps {
  imageUrl: string;
  description: string;
  uom: string;
  price: string;
}

/**
 * The product card as a customer sees it on a phone (issue #142): the same
 * full-width, 200px-tall photo cropped with object-fit: cover, then the name,
 * unit, price and Add to Cart button.
 */
export const MobileCardPreview: React.FC<MobileCardPreviewProps> = ({
  imageUrl,
  description,
  uom,
  price,
}) => (
  <div
    aria-label="Mobile product card preview"
    style={{
      width: MOBILE_CARD_WIDTH + 32,
      maxWidth: '100%',
      padding: '20px 16px',
      background: '#f2f3f5',
      border: '8px solid #1f1f1f',
      borderRadius: 28,
    }}
  >
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        overflow: 'hidden',
        boxShadow: '0 1px 4px rgba(0, 0, 0, 0.12)',
      }}
    >
      <img
        src={imageUrl}
        alt={description}
        style={{ display: 'block', width: '100%', height: MOBILE_CARD_IMAGE_HEIGHT, objectFit: 'cover' }}
        onError={(e) => {
          (e.target as HTMLImageElement).src = '/images/product-placeholder.svg';
        }}
      />
      <div style={{ padding: '12px 16px' }}>
        <Text strong ellipsis style={{ display: 'block', fontSize: 16 }}>
          {description}
        </Text>
        <Text type="secondary" style={{ display: 'block' }}>
          {uom}
        </Text>
        <Text strong style={{ color: 'var(--dasoon-primary)', fontFamily: 'var(--font-headers)' }}>
          {price}
        </Text>
      </div>
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '10px 16px' }}>
        <Button
          type="primary"
          className="dasoon-btn-primary"
          icon={<ShoppingCartOutlined />}
          block
          tabIndex={-1}
          style={{ pointerEvents: 'none' }}
        >
          Add to Cart
        </Button>
      </div>
    </div>
  </div>
);

interface MobilePhotoCropperProps {
  open: boolean;
  /** The photo to frame, normally the product's main photo. */
  sourceUrl: string;
  saving: boolean;
  onCancel: () => void;
  onSave: (file: File) => void;
}

/**
 * Drag and zoom a photo inside the mobile card frame, then save exactly what
 * the frame shows as the mobile photo (issue #142). Saves admins cropping in
 * another app and uploading again.
 */
export const MobilePhotoCropper: React.FC<MobilePhotoCropperProps> = ({
  open,
  sourceUrl,
  saving,
  onCancel,
  onSave,
}) => {
  const imageRef = useRef<HTMLImageElement | null>(null);
  const dragRef = useRef<{ pointerX: number; pointerY: number; offset: Offset } | null>(null);
  const [natural, setNatural] = useState<Size | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState<Offset>({ x: 0, y: 0 });
  const [failed, setFailed] = useState(false);

  // Start every session centred and fully zoomed out.
  useEffect(() => {
    if (open) {
      setNatural(null);
      setZoom(1);
      setFailed(false);
    }
  }, [open, sourceUrl]);

  const baseScale = natural ? coverScale(natural, FRAME) : 1;
  // Zoom 1 fills the card, as the storefront crops it. Below 1 shows more of
  // the photo, down to all of it.
  const minZoom = natural ? containScale(natural, FRAME) / baseScale : 1;
  const scale = baseScale * zoom;

  const handleLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    const size = { width: img.naturalWidth, height: img.naturalHeight };
    setNatural(size);
    setOffset(centredOffset(size, coverScale(size, FRAME), FRAME));
  };

  const changeZoom = (next: number) => {
    if (!natural) return;
    const clamped = Math.min(MAX_ZOOM, Math.max(minZoom, next));
    setOffset(zoomAboutCentre(offset, scale, baseScale * clamped, natural, FRAME));
    setZoom(clamped);
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!natural) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { pointerX: e.clientX, pointerY: e.clientY, offset };
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || !natural) return;
    setOffset(
      clampOffset(
        { x: drag.offset.x + e.clientX - drag.pointerX, y: drag.offset.y + e.clientY - drag.pointerY },
        natural,
        scale,
        FRAME
      )
    );
  };

  const endDrag = () => {
    dragRef.current = null;
  };

  const handleSave = async () => {
    const img = imageRef.current;
    if (!img || !natural) return;

    const canvas = document.createElement('canvas');
    canvas.width = FRAME.width * MOBILE_PHOTO_OUTPUT_SCALE;
    canvas.height = FRAME.height * MOBILE_PHOTO_OUTPUT_SCALE;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      message.error('This browser could not prepare the photo. Please try another browser.');
      return;
    }
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    const { dx, dy, dw, dh } = destRect(offset, natural, scale, MOBILE_PHOTO_OUTPUT_SCALE);
    ctx.drawImage(img, dx, dy, dw, dh);

    // Step the quality down until it fits the upload limit.
    for (const quality of [0.9, 0.8, 0.7, 0.6]) {
      const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
      if (blob && blob.size <= MAX_UPLOAD_BYTES) {
        onSave(new File([blob], 'mobile-photo.jpg', { type: 'image/jpeg' }));
        return;
      }
    }
    message.error('The framed photo is too large to save. Try zooming out a little.');
  };

  return (
    <Modal
      open={open}
      title="Adjust photo for mobile"
      onCancel={onCancel}
      width={460}
      destroyOnHidden
      footer={[
        <Button key="cancel" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>,
        <Button key="save" type="primary" onClick={handleSave} loading={saving} disabled={!natural || failed}>
          Save as mobile photo
        </Button>,
      ]}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Text type="secondary">
          Drag the photo to choose what shows in the product card on a phone, and use the slider to zoom.
          Slide left to show the whole photo; any space around it is white. The frame is the exact
          shape of the card.
        </Text>
        <div
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endDrag}
          onPointerCancel={endDrag}
          style={{
            position: 'relative',
            // Exact card size: the crop maths assumes the frame is not resized.
            width: FRAME.width,
            height: FRAME.height,
            overflow: 'hidden',
            borderRadius: 8,
            // White, as saved: what shows around a zoomed-out photo.
            background: '#ffffff',
            cursor: natural ? 'grab' : 'default',
            touchAction: 'none',
            userSelect: 'none',
            margin: '0 auto',
            outline: '2px solid var(--dasoon-primary, #FFAE00)',
          }}
        >
          {failed ? (
            <Text type="secondary" style={{ display: 'block', padding: 24, textAlign: 'center' }}>
              The photo could not be loaded.
            </Text>
          ) : (
            <img
              ref={imageRef}
              src={sourceUrl}
              alt="Photo to frame"
              draggable={false}
              onLoad={handleLoad}
              onError={() => setFailed(true)}
              style={{
                position: 'absolute',
                left: offset.x,
                top: offset.y,
                width: natural ? natural.width * scale : '100%',
                height: natural ? natural.height * scale : 'auto',
                maxWidth: 'none',
                visibility: natural ? 'visible' : 'hidden',
                pointerEvents: 'none',
              }}
            />
          )}
        </div>
        <Space style={{ width: '100%' }} align="center">
          <ZoomOutOutlined />
          <Slider
            min={Math.min(1, minZoom)}
            max={MAX_ZOOM}
            step={0.01}
            marks={natural && minZoom < 1 ? { [minZoom]: 'Whole', 1: 'Fill' } : undefined}
            value={zoom}
            onChange={changeZoom}
            disabled={!natural}
            tooltip={{ formatter: null }}
            style={{ width: 300 }}
          />
          <ZoomInOutlined />
        </Space>
      </Space>
    </Modal>
  );
};
